import { useState, useEffect, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { arrayMove } from '@dnd-kit/sortable';
import { toast } from 'sonner';

// LocalStorage key
const STORAGE_KEY = 'wherewasi_data';

// Auto-detect task category from text
export const detectCategory = (text) => {
  const lower = text.toLowerCase();
  const emailPatterns = ['email', 'emails', 'e-mail', 'e-mails', 'mail to', 'send mail', 'reply to', 'forward to', 'inbox', 'draft email', 'write to', 'mailing'];
  const phonePatterns = ['call', 'calls', 'calling', 'phone', 'ring', 'dial', 'voicemail', 'phone call', 'callback', 'call back'];
  if (emailPatterns.some(p => lower.includes(p))) return 'email';
  if (phonePatterns.some(p => {
    const idx = lower.indexOf(p);
    if (idx === -1) return false;
    const before = idx > 0 ? lower[idx - 1] : ' ';
    const after = idx + p.length < lower.length ? lower[idx + p.length] : ' ';
    return /[\s,.\-!?;:()']/.test(before) || idx === 0 ? /[\s,.\-!?;:()']/.test(after) || idx + p.length === lower.length : false;
  })) return 'phone';
  return 'action';
};

// Demo data with varied ages to show thread aging indicators
const getDemoData = () => {
  const now = new Date();
  const daysAgo = (days) => new Date(now - days * 24 * 60 * 60 * 1000).toISOString();
  
  return {
    boards: [{
      id: 'demo-board',
      name: 'My Focus Board',
      lists: [
        {
          id: 'list-1',
          name: 'To Do',
          cards: [
            { id: 'card-1', content: 'Finish writing that email to Sarah', notes: '', createdAt: daysAgo(0), isCritical: true, category: 'email' },
            { id: 'card-2', content: 'Review project proposal', notes: 'Due by Friday', createdAt: daysAgo(2), isCritical: false },
          ]
        },
        {
          id: 'list-2', 
          name: 'Doing',
          cards: [
            { id: 'card-3', content: 'Research competitor features', notes: 'Started recently', createdAt: daysAgo(1), isCritical: false, category: 'action' },
            { id: 'card-5', content: 'Draft weekly rhythm plan', notes: 'Taking time, and that\'s okay', createdAt: daysAgo(5), isCritical: true, category: 'action' },
            { id: 'card-6', content: 'Email Sarah about project update', notes: '', createdAt: daysAgo(2), isCritical: false, category: 'email' },
            { id: 'card-7', content: 'Call accountant re: invoices', notes: '', createdAt: daysAgo(3), isCritical: false, category: 'phone' },
          ]
        },
        {
          id: 'list-3',
          name: 'Done',
          cards: [
            { id: 'card-4', content: 'Set up meeting with team', createdAt: daysAgo(3), completedAt: daysAgo(1), isCritical: false },
          ]
        }
      ]
    }],
    currentBoardId: 'demo-board'
  };
};

const getEmptyData = () => ({
  boards: [{
    id: uuidv4(),
    name: 'My Focus Board',
    lists: [
      { id: uuidv4(), name: 'To Do', cards: [] },
      { id: uuidv4(), name: 'Doing', cards: [] },
      { id: uuidv4(), name: 'Done', cards: [] }
    ]
  }],
  currentBoardId: null
});

// Helper to process board data - migrate old column names
const processListData = (data) => {
  if (!data?.boards) return data;
  
  // Migrate "In Progress" to "DOING" for consistency
  data.boards.forEach(board => {
    if (board.lists) {
      board.lists.forEach(list => {
        if (list.name?.toLowerCase() === 'in progress') {
          list.name = 'DOING';
        }
      });
    }
  });
  
  return data;
};

// LocalStorage key for last active task (used by /now page)
const LAST_ACTIVE_KEY = 'wwi_last_active_task';

// Save last active task to localStorage for /now page instant retrieval
const saveLastActiveTask = (task, listName) => {
  if (!task || !task.id) return;
  try {
    const taskData = {
      taskId: task.id,
      content: task.content,
      category: task.category || 'action',
      listName: listName || 'Doing',
      timestamp: task.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(LAST_ACTIVE_KEY, JSON.stringify(taskData));
  } catch (e) {
    console.error('Error saving last active task:', e);
  }
};

/**
 * useBoardData - Manages all board data state and persistence
 * Handles local storage, cloud sync, and all CRUD operations
 */
export const useBoardData = ({
  isDemo = false,
  isAuthenticated = false,
  authLoading = false,
  fetchUserData,
  saveUserData,
  setFireCounterFromCloud,
  justSignedIn = false,
  trackAndUpdateThread,
  nudgeSystem,
  incrementTaskCount,
  triggerFirstTaskAdded,
}) => {
  const [data, setData] = useState(null);
  const [syncStatus, setSyncStatus] = useState('idle'); // idle, syncing, synced, error
  const [isLoadingCloud, setIsLoadingCloud] = useState(false);
  
  const hasLoadedCloudData = useRef(false);
  const saveTimeoutRef = useRef(null);

  // Get current board
  const currentBoard = data?.boards?.[0];

  // Auto-save to cloud (debounced)
  const saveToCloud = useCallback(async (boardData) => {
    if (!isAuthenticated || isDemo) return;
    
    setSyncStatus('syncing');
    try {
      const fireCounter = JSON.parse(localStorage.getItem('wwi_fire_counter') || '{}');
      const settings = {
        sounds: localStorage.getItem('wwi_sounds') !== 'false',
        animations: localStorage.getItem('wwi_animations') !== 'false',
        celebrations: localStorage.getItem('wwi_celebrations') !== 'false'
      };
      
      const success = await saveUserData(boardData, fireCounter, settings);
      setSyncStatus(success ? 'synced' : 'error');
      
      // Reset status after a delay
      setTimeout(() => setSyncStatus('idle'), 2000);
    } catch (error) {
      console.error('Cloud save error:', error);
      setSyncStatus('error');
    }
  }, [isAuthenticated, isDemo, saveUserData]);

  // Debounced save function
  const debouncedSaveToCloud = useCallback((boardData) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveToCloud(boardData);
    }, 2000); // Save 2 seconds after last change
  }, [saveToCloud]);

  // Load data on mount - auth-aware
  useEffect(() => {
    const loadData = async () => {
      console.log('[useBoardData] loadData called - isDemo:', isDemo, 'authLoading:', authLoading, 'isAuthenticated:', isAuthenticated);
      
      // Demo mode - always use demo data
      if (isDemo) {
        setData(getDemoData());
        return;
      }

      // Wait for auth to finish loading
      if (authLoading) {
        console.log('[useBoardData] Waiting for auth to finish loading...');
        return;
      }

      // Get local data first
      let localData = null;
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          localData = JSON.parse(stored);
        } catch {
          localData = getEmptyData();
        }
      } else {
        localData = getEmptyData();
      }

      // If not authenticated, use local data only
      if (!isAuthenticated) {
        console.log('[useBoardData] Not authenticated, using local data');
        setData(processListData(localData));
        hasLoadedCloudData.current = false;
        return;
      }

      // Authenticated user - fetch cloud data
      console.log('[useBoardData] User is authenticated, fetching cloud data...');
      if (!hasLoadedCloudData.current) {
        setIsLoadingCloud(true);
        try {
          const cloudResponse = await fetchUserData();
          
          if (cloudResponse?.boards) {
            const cloudBoardData = cloudResponse.boards;
            const cloudFireCounter = cloudResponse.fire_counter;
            
            // Check if local data has content (non-empty boards)
            const localHasContent = localData?.boards?.[0]?.lists?.some(
              list => list.cards && list.cards.length > 0
            );
            
            // Check if cloud data has content
            const cloudHasContent = cloudBoardData?.boards?.[0]?.lists?.some(
              list => list.cards && list.cards.length > 0
            );
            
            // If user just signed in AND has local content AND cloud is empty
            if (justSignedIn && localHasContent && !cloudHasContent) {
              // Cloud is empty, migrate local data
              setData(processListData(localData));
              toast.success('Your local threads have been saved to your account!');
              debouncedSaveToCloud(processListData(localData));
            } else if (cloudHasContent) {
              // Cloud has data, use it (prioritize cloud for cross-device sync)
              const reorderedData = processListData(cloudBoardData);
              setData(reorderedData);
              localStorage.setItem(STORAGE_KEY, JSON.stringify(reorderedData));
              if (justSignedIn) {
                toast.success('Welcome back! Your threads are synced.');
              }
            } else {
              // Both empty or cloud empty - use local
              setData(processListData(localData));
            }
            
            // Restore fire counter from cloud
            if (cloudFireCounter) {
              localStorage.setItem('wwi_fire_counter', JSON.stringify(cloudFireCounter));
              if (setFireCounterFromCloud) {
                setFireCounterFromCloud(cloudFireCounter);
              }
            }
          } else {
            // No cloud data - use local and save to cloud if user has content
            setData(processListData(localData));
            const localHasContent = localData?.boards?.[0]?.lists?.some(
              list => list.cards && list.cards.length > 0
            );
            if (localHasContent) {
              debouncedSaveToCloud(processListData(localData));
              toast.success('Your threads are now synced to your account!');
            }
          }
          
          hasLoadedCloudData.current = true;
        } catch (error) {
          console.error('Error loading cloud data:', error);
          setData(localData);
        } finally {
          setIsLoadingCloud(false);
        }
      }
    };

    loadData();
  }, [isDemo, authLoading, isAuthenticated, fetchUserData, justSignedIn, debouncedSaveToCloud, setFireCounterFromCloud]);

  // Save to localStorage AND cloud whenever data changes
  useEffect(() => {
    if (data && !isDemo && !isLoadingCloud) {
      // Always save to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      
      // Auto-save to cloud if authenticated
      if (isAuthenticated && hasLoadedCloudData.current) {
        debouncedSaveToCloud(data);
      }
    }
  }, [data, isDemo, isAuthenticated, debouncedSaveToCloud, isLoadingCloud]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Find which list contains a card
  const findListByCardId = useCallback((cardId) => {
    if (!currentBoard) return null;
    for (const list of currentBoard.lists) {
      if (list.cards.some(c => c.id === cardId)) {
        return list;
      }
    }
    return null;
  }, [currentBoard]);

  // Get all critical tasks with their list context
  const getCriticalTasks = useCallback(() => {
    if (!currentBoard) return [];
    const criticalTasks = [];
    
    for (const list of currentBoard.lists) {
      const isDoneList = list.name.toLowerCase() === 'done';
      for (const card of list.cards) {
        if (card.isCritical) {
          criticalTasks.push({
            ...card,
            listName: list.name,
            isDone: isDoneList
          });
        }
      }
    }
    
    // Sort by most recent first (non-done tasks prioritized)
    return criticalTasks.sort((a, b) => {
      // Non-done tasks come first
      if (a.isDone !== b.isDone) return a.isDone ? 1 : -1;
      // Then by creation date (most recent first)
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  }, [currentBoard]);

  // Add a new card
  const handleAddCard = useCallback((listId, content, isImportant = false) => {
    const newCard = {
      id: uuidv4(),
      content,
      notes: '',
      createdAt: new Date().toISOString(),
      isCritical: isImportant,
      category: detectCategory(content)
    };
    
    setData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      const list = newData.boards[0].lists.find(l => l.id === listId);
      if (list) {
        list.cards.push(newCard);
      }
      return newData;
    });
    
    // Track engagement for sync invitation
    if (!isDemo && incrementTaskCount) {
      incrementTaskCount();
    }
    
    // Trigger first task onboarding event
    if (triggerFirstTaskAdded) {
      triggerFirstTaskAdded();
    }
    
    // Track as current thread
    if (trackAndUpdateThread) {
      trackAndUpdateThread(newCard.id);
    }
    
    return newCard;
  }, [isDemo, incrementTaskCount, triggerFirstTaskAdded, trackAndUpdateThread]);

  // Edit a card (open modal - returns the card to edit)
  const handleEditCard = useCallback((card) => {
    return card;
  }, []);

  // Save edited card
  const handleSaveCard = useCallback(async (updatedCard, oldCard) => {
    setData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      for (const list of newData.boards[0].lists) {
        const cardIndex = list.cards.findIndex(c => c.id === updatedCard.id);
        if (cardIndex !== -1) {
          list.cards[cardIndex] = updatedCard;
          break;
        }
      }
      return newData;
    });
    
    // Track as current thread
    if (trackAndUpdateThread) {
      trackAndUpdateThread(updatedCard.id);
    }
    
    // Handle nudge changes if nudgeSystem is provided
    if (nudgeSystem) {
      const oldNudgeEnabled = oldCard?.nudgeEnabled || false;
      const newNudgeEnabled = updatedCard.nudgeEnabled || false;
      const oldImportant = oldCard?.isCritical || false;
      const newImportant = updatedCard.isCritical || false;
      
      // If nudge was enabled
      if (newNudgeEnabled && !oldNudgeEnabled && newImportant) {
        await nudgeSystem.enableNudge(updatedCard.id, updatedCard.content);
        nudgeSystem.recordActivity('edit');
      }
      // If nudge was disabled or task unmarked as important
      else if ((!newNudgeEnabled && oldNudgeEnabled) || (!newImportant && oldImportant)) {
        await nudgeSystem.cancelNudge(updatedCard.id);
      }
    }
  }, [trackAndUpdateThread, nudgeSystem]);

  // Delete a card
  const handleDeleteCard = useCallback((cardId, listId) => {
    setData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      const list = newData.boards[0].lists.find(l => l.id === listId);
      if (list) {
        list.cards = list.cards.filter(c => c.id !== cardId);
      }
      return newData;
    });
  }, []);

  // Toggle critical status for a card
  const handleToggleCritical = useCallback((cardId) => {
    setData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      for (const list of newData.boards[0].lists) {
        const card = list.cards.find(c => c.id === cardId);
        if (card) {
          card.isCritical = !card.isCritical;
          // If unmarking as important, cancel any pending nudge
          if (!card.isCritical && card.nudgeEnabled) {
            card.nudgeEnabled = false;
            nudgeSystem?.cancelNudge(cardId);
          }
          break;
        }
      }
      return newData;
    });
  }, [nudgeSystem]);

  // Move card to a specific list
  const handleMoveToList = useCallback((cardId, fromListId, targetListName, callbacks = {}) => {
    if (!currentBoard) return;
    
    const { decrementCount, playWaveSwoosh, playProgress } = callbacks;
    
    // Find the target list by name
    const targetList = currentBoard.lists.find(l => l.name.toLowerCase() === targetListName.toLowerCase());
    if (!targetList) return;
    
    // Find the source list and card
    const fromList = currentBoard.lists.find(l => l.id === fromListId);
    if (!fromList) return;
    
    const card = fromList.cards.find(c => c.id === cardId);
    if (!card) return;
    
    // Check if moving from Done list (to decrement fire counter)
    const isFromDone = fromList.name.toLowerCase() === 'done';
    
    // Move card to target list
    setData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      const board = newData.boards[0];
      const sourceList = board.lists.find(l => l.id === fromListId);
      const destList = board.lists.find(l => l.name.toLowerCase() === targetListName.toLowerCase());
      
      if (sourceList && destList) {
        // Remove from source
        sourceList.cards = sourceList.cards.filter(c => c.id !== cardId);
        const isDoneTarget = targetListName.toLowerCase() === 'done';
        // Add completedAt when moving to Done, clear it otherwise
        const cardToAdd = { ...card };
        if (isDoneTarget) {
          cardToAdd.completedAt = new Date().toISOString();
        } else {
          delete cardToAdd.completedAt;
          cardToAdd.createdAt = new Date().toISOString();
        }
        // Auto-detect category when entering Doing column
        const isMovingToDoing = targetListName.toLowerCase() === 'doing' || targetListName.toLowerCase() === 'in progress';
        if (isMovingToDoing) {
          const detected = detectCategory(card.content);
          if (detected !== 'action') {
            cardToAdd.category = detected;
          }
        }
        // Add to target (at the beginning so it's visible)
        destList.cards.unshift(cardToAdd);
      }
      
      return newData;
    });
    
    // Decrement fire counter when undoing a completion
    if (isFromDone && decrementCount) {
      decrementCount();
    }
    
    // Play appropriate sound and track thread
    const isMovingToDoing = targetListName.toLowerCase() === 'doing' || targetListName.toLowerCase() === 'in progress';
    if (isMovingToDoing) {
      if (playWaveSwoosh) playWaveSwoosh();
      // Track as current thread when moving to Doing
      if (trackAndUpdateThread) {
        trackAndUpdateThread(cardId);
      }
      // Save last active task for /now page
      const cardWithCategory = { ...card, category: card.category || detectCategory(card.content) };
      saveLastActiveTask(cardWithCategory, targetListName);
    } else {
      if (playProgress) playProgress();
    }
  }, [currentBoard, trackAndUpdateThread]);

  // Reorder cards within the same list (during drag)
  const reorderCardsInList = useCallback((listId, activeId, overId) => {
    const list = currentBoard?.lists.find(l => l.id === listId);
    if (!list) return;
    
    const activeCardIndex = list.cards.findIndex(c => c.id === activeId);
    const overCardIndex = list.cards.findIndex(c => c.id === overId);
    
    if (activeCardIndex === -1 || overCardIndex === -1 || activeCardIndex === overCardIndex) return;
    
    const isDoingColumn = list.name.toLowerCase() === 'doing' || list.name.toLowerCase() === 'in progress';
    
    setData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      const board = newData.boards[0];
      const targetList = board.lists.find(l => l.id === listId);
      
      // For the Doing column with grouped subsections, infer category from neighbors after move
      if (isDoingColumn) {
        const hasComms = targetList.cards.some(c => c.category === 'email' || c.category === 'phone');
        if (hasComms) {
          const catOrder = { action: 0, email: 1, phone: 2 };
          const sorted = [...targetList.cards].sort((a, b) => (catOrder[a.category || 'action'] || 0) - (catOrder[b.category || 'action'] || 0));
          const sortedActiveIdx = sorted.findIndex(c => c.id === activeId);
          const sortedOverIdx = sorted.findIndex(c => c.id === overId);
          const reordered = arrayMove(sorted, sortedActiveIdx, sortedOverIdx);
          // Infer category from the card above the new position
          const movedIdx = sortedOverIdx;
          const above = movedIdx > 0 ? reordered[movedIdx - 1] : null;
          const below = movedIdx < reordered.length - 1 ? reordered[movedIdx + 1] : null;
          const movedCard = reordered[movedIdx];
          if (above) movedCard.category = above.category || 'action';
          else if (below) movedCard.category = below.category || 'action';
          targetList.cards = reordered;
        } else {
          targetList.cards = arrayMove(targetList.cards, activeCardIndex, overCardIndex);
        }
      } else {
        targetList.cards = arrayMove(targetList.cards, activeCardIndex, overCardIndex);
      }
      return newData;
    });
  }, [currentBoard]);

  // Move card to different list (during drag)
  const moveCardToList = useCallback((cardId, fromListId, toListId, insertIndex = -1) => {
    const fromList = currentBoard?.lists.find(l => l.id === fromListId);
    const toList = currentBoard?.lists.find(l => l.id === toListId);
    if (!fromList || !toList) return null;
    
    const card = fromList.cards.find(c => c.id === cardId);
    if (!card) return null;
    
    const isDoneList = toList.name.toLowerCase() === 'done';
    const isFromDone = fromList.name.toLowerCase() === 'done';
    const isInProgressList = toList.name.toLowerCase() === 'doing' || toList.name.toLowerCase() === 'in progress';
    const isFromToDo = fromList.name.toLowerCase() === 'to do';
    
    setData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      const board = newData.boards[0];
      const sourceList = board.lists.find(l => l.id === fromListId);
      const destList = board.lists.find(l => l.id === toListId);
      
      // Remove from source
      sourceList.cards = sourceList.cards.filter(c => c.id !== cardId);
      
      // Add to destination — stamp completedAt if moving to Done
      const cardToAdd = isDoneList ? { ...card, completedAt: new Date().toISOString() } : { ...card };
      // Clear completedAt if moving out of Done
      if (isFromDone && !isDoneList) delete cardToAdd.completedAt;
      // Auto-detect category when entering Doing column
      if (isInProgressList) {
        const detected = detectCategory(cardToAdd.content);
        if (detected !== 'action') {
          cardToAdd.category = detected;
        }
      }
      
      if (insertIndex !== -1) {
        destList.cards.splice(insertIndex, 0, cardToAdd);
      } else {
        destList.cards.push(cardToAdd);
      }
      
      return newData;
    });
    
    // Return movement info for sound/haptic handling
    return {
      isDoneList,
      isFromDone,
      isInProgressList,
      isFromToDo,
      card,
      nudgeEnabled: card.nudgeEnabled
    };
  }, [currentBoard]);

  // List operations
  const handleAddList = useCallback((listName) => {
    if (!listName.trim()) return;
    
    const newList = {
      id: uuidv4(),
      name: listName.trim(),
      cards: []
    };
    
    setData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      newData.boards[0].lists.push(newList);
      return newData;
    });
  }, []);

  const handleRenameList = useCallback((listId, newName) => {
    setData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      const list = newData.boards[0].lists.find(l => l.id === listId);
      if (list) {
        list.name = newName;
      }
      return newData;
    });
  }, []);

  const handleDeleteList = useCallback((listId) => {
    setData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      newData.boards[0].lists = newData.boards[0].lists.filter(l => l.id !== listId);
      return newData;
    });
  }, []);

  // Reorder columns
  const handleReorderColumns = useCallback((oldIndex, newIndex) => {
    setData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      newData.boards[0].lists = arrayMove(newData.boards[0].lists, oldIndex, newIndex);
      
      // Save column order to localStorage
      const columnOrder = newData.boards[0].lists.map(l => l.id);
      localStorage.setItem('wwi_column_order', JSON.stringify(columnOrder));
      
      return newData;
    });
  }, []);

  // Reset board to empty state
  const resetBoard = useCallback(() => {
    setData(getEmptyData());
  }, []);

  return {
    // State
    data,
    setData,
    currentBoard,
    syncStatus,
    isLoadingCloud,
    hasLoadedCloudData: hasLoadedCloudData.current,
    
    // Utilities
    findListByCardId,
    getCriticalTasks,
    detectCategory,
    
    // Card operations
    handleAddCard,
    handleEditCard,
    handleSaveCard,
    handleDeleteCard,
    handleToggleCritical,
    handleMoveToList,
    
    // Drag operations
    reorderCardsInList,
    moveCardToList,
    
    // List operations
    handleAddList,
    handleRenameList,
    handleDeleteList,
    handleReorderColumns,
    
    // Board operations
    resetBoard,
    
    // Cloud operations
    saveToCloud,
    debouncedSaveToCloud,
  };
};

export default useBoardData;
